import React, { useState, useMemo } from 'react';
import {
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Checkbox,
  Divider,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close, Search, Star, StarBorder } from '@mui/icons-material';
import { useGettyFavorites } from '../hooks/useGettyFavorites';
import { VocabularyType } from '../utils/gettyFavoritesApi';
import { Field } from './edit-object/design/Field';

interface AutocompleteFieldMultiSelectWithFavoritesProps {
  label: string;
  value: string[];
  options: string[];
  onChange: (value: string[]) => void;
  loading?: boolean;
  vocabularyType: VocabularyType;
  /**
   * 'mui' (default) renders the legacy MUI TextField trigger with a
   * notched outline label. 'design' wraps the trigger in the shared
   * Field primitive so the label is always visible and uppercase, in
   * line with the rest of the edit-object hi-fi panel.
   */
  variant?: 'mui' | 'design';
}

export const AutocompleteFieldMultiSelectWithFavorites = ({
  label,
  value,
  options,
  onChange,
  loading,
  vocabularyType,
  variant = 'mui',
}: AutocompleteFieldMultiSelectWithFavoritesProps) => {
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [freetextMode, setFreetextMode] = useState(false);
  const [freetextValue, setFreetextValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const { isFavorite, toggleFavorite, getFavoritesForType } = useGettyFavorites();
  const favorites = useMemo(
    () => getFavoritesForType(vocabularyType),
    [vocabularyType, getFavoritesForType]
  );

  // Focus search input when dialog opens
  React.useEffect(() => {
    if (dialogOpen && searchInputRef.current) {
      // Use setTimeout to ensure the input is ready for focus
      const timeoutId = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [dialogOpen]);

  const handleOpen = () => {
    setDialogOpen(true);
    setSelectedValues([...value]);
    setFreetextMode(false);
    setSearchValue('');
  };

  const handleClose = () => {
    setDialogOpen(false);
    setFreetextMode(false);
    setFreetextValue('');
    setSearchValue('');
  };

  const handleSelect = () => {
    if (freetextMode && freetextValue.trim()) {
      const newValues = selectedValues.includes(freetextValue)
        ? selectedValues
        : [...selectedValues, freetextValue];
      onChange(newValues);
    } else {
      onChange(selectedValues);
    }
    handleClose();
  };

  const handleToggleOption = (option: string) => {
    setSelectedValues(prev =>
      prev.includes(option)
        ? prev.filter(v => v !== option)
        : [...prev, option]
    );
  };

  const handleToggleFavorite = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite(vocabularyType, optionValue, optionValue, []);
  };

  const filteredOptions = useMemo(() => {
    if (!searchValue.trim()) return options || [];
    return (options || []).filter((option) =>
      option.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [options, searchValue]);

  const displayValue = value.length > 0 ? value.join(', ') : '';

  return (
    <>
      {variant === 'design' ? (
        <Field label={label}>
          <div
            className="input"
            role="button"
            aria-label={label}
            tabIndex={loading ? -1 : 0}
            aria-disabled={loading || undefined}
            onClick={loading ? undefined : handleOpen}
            onKeyDown={(e) => {
              if (loading) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpen();
              }
            }}
            style={{
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 4,
              minHeight: 38,
              opacity: loading ? 0.6 : 1,
            }}
          >
            {value.length > 0 ? (
              value.map((v) => <Chip key={v} label={v} size="small" />)
            ) : (
              <span style={{ color: 'var(--slate-500)' }}>
                Click to select {label.toLowerCase()}
              </span>
            )}
          </div>
        </Field>
      ) : (
        <TextField
          label={label}
          value={displayValue}
          onClick={handleOpen}
          variant="outlined"
          fullWidth
          disabled={loading}
          placeholder={`Click to select ${label.toLowerCase()}`}
          InputProps={{
            readOnly: true,
            startAdornment: value.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mr: 1 }}>
                {value.map((v) => (
                  <Chip key={v} label={v} size="small" />
                ))}
              </Box>
            ) : undefined,
          }}
          sx={{
            cursor: 'pointer',
            '& .MuiOutlinedInput-root': {
              cursor: 'pointer',
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />
      )}

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth fullScreen={fullScreenMobile} disableAutoFocus={false}>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          Select {label}
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            // Flex column so the options list is the single scroll surface.
            // Avoids a second scrollbar on short viewports while keeping the
            // favorites section at the bottom always visible.
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {!freetextMode ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              {/* Selected chips */}
              {selectedValues.length > 0 && (
                <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5, flexShrink: 0 }}>
                  {selectedValues.map((v) => (
                    <Chip
                      key={v}
                      label={v}
                      size="small"
                      onDelete={() => handleToggleOption(v)}
                      color="primary"
                    />
                  ))}
                </Box>
              )}

              <TextField
                ref={searchInputRef}
                placeholder={`Search ${label.toLowerCase()}...`}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                fullWidth
                sx={{ mb: 2, flexShrink: 0 }}
                autoFocus
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />

              <Box
                sx={{ flex: 1, minHeight: 0, overflow: 'auto', border: 1, borderColor: 'divider' }}
              >
                <List dense>
                  {filteredOptions.length === 0 ? (
                    <ListItem>
                      <ListItemText
                        primary={
                          searchValue
                            ? `No results found for "${searchValue}"`
                            : 'No options available'
                        }
                        sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                      />
                    </ListItem>
                  ) : (
                    filteredOptions.map((option, index) => {
                      const isSelected = selectedValues.includes(option);
                      const isOptionFavorite = isFavorite(vocabularyType, option);
                      return (
                        <ListItem key={index} disablePadding>
                          <ListItemButton
                            selected={isSelected}
                            onClick={() => handleToggleOption(option)}
                            sx={{
                              '&.Mui-selected': {
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText',
                                '&:hover': {
                                  bgcolor: 'primary.main',
                                },
                              },
                            }}
                          >
                            <Checkbox
                              checked={isSelected}
                              size="small"
                              sx={{ mr: 1, p: 0.5 }}
                              tabIndex={-1}
                            />
                            <Tooltip
                              title={isOptionFavorite ? 'Remove from favorites' : 'Add to favorites'}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => handleToggleFavorite(option, e)}
                                sx={{ mr: 1, p: 0.5 }}
                              >
                                {isOptionFavorite ? (
                                  <Star fontSize="small" data-testid="StarIcon" sx={{ color: 'gold' }} />
                                ) : (
                                  <StarBorder fontSize="small" data-testid="StarBorderIcon" sx={{ color: 'action.disabled' }} />
                                )}
                              </IconButton>
                            </Tooltip>
                            <ListItemText
                              primary={option}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })
                  )}
                </List>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 2,
                  pt: 1,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {selectedValues.length} selected | {filteredOptions.length} of {options?.length || 0} options
                </Typography>
                <Link
                  component="button"
                  onClick={() => {
                    setFreetextMode(true);
                    setFreetextValue('');
                  }}
                  sx={{ textDecoration: 'underline' }}
                >
                  Enter custom text
                </Link>
              </Box>

              {favorites.length > 0 && (
                <>
                  <Divider role="presentation" sx={{ my: 2 }} />
                  <Box data-testid="favorites-section" sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Star fontSize="small" sx={{ color: 'gold', mr: 0.5 }} />
                      <Typography variant="subtitle2" color="text.secondary">
                        Favorites
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {favorites.map((fav) => {
                        const isFavSelected = selectedValues.includes(fav.textValue);
                        return (
                          <Chip
                            key={fav.uri}
                            label={fav.textValue}
                            size="small"
                            onClick={() => handleToggleOption(fav.textValue)}
                            onDelete={() =>
                              toggleFavorite(vocabularyType, fav.uri, fav.textValue, fav.breadcrumbs)
                            }
                            deleteIcon={<Star fontSize="small" sx={{ color: 'gold' }} />}
                            variant={isFavSelected ? 'filled' : 'outlined'}
                            color={isFavSelected ? 'primary' : 'default'}
                            sx={{ cursor: 'pointer' }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>
                Enter Custom {label}
              </Typography>
              <TextField
                label="Text"
                value={freetextValue}
                onChange={(e) => setFreetextValue(e.target.value)}
                variant="outlined"
                fullWidth
                autoFocus
              />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSelect}
            variant="contained"
            disabled={freetextMode ? !freetextValue.trim() : false}
          >
            {freetextMode ? 'Add & Select' : 'Select'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
